import { ChannelType } from './extranet.model';
import { OrderSourceFilter, isOrderSourceFilter } from './order-sources.model';

/**
 * Agency-wide orders surface (`GET /extranet/orders` + `/summary`, F0 BE; consumed by the
 * Reservations list for AGENCY_MANAGER since WI #3303). Kept in its own file so sibling stories
 * touching `extranet.model.ts` never collide with this one.
 */

/** Every booking status the BE can hold on an order header. PENDING = on option (handoff §12):
 *  rooms held, outside every money figure, waiting for Confirm booking / Release. */
export type BookingStatus = 'CONFIRMED' | 'CHECKED_IN' | 'CHECKED_OUT' | 'CANCELLED' | 'NO_SHOW' | 'PENDING';

export const BOOKING_STATUSES: readonly BookingStatus[] = [
  'CONFIRMED', 'CHECKED_IN', 'CHECKED_OUT', 'CANCELLED', 'NO_SHOW', 'PENDING',
];

/** Stay statuses the Extranet no longer manages (owner decision 2026-10-09, WI 3850): the hotel's
 *  front desk runs check-in, check-out and no-show in its own system and eZee never receives them.
 *  The BE still accepts and returns them, so older bookings may carry one; the Extranet reads them as
 *  CONFIRMED — the way every BE figure already counts them (a live stay: revenue, rooms, balance). */
export const LEGACY_STAY_STATUSES = ['CHECKED_IN', 'CHECKED_OUT', 'NO_SHOW'] as const;
export type LegacyStayStatus = typeof LEGACY_STAY_STATUSES[number];

/** The status the Extranet shows for a BE status: a legacy stay status reads CONFIRMED, any other
 *  passes through ('' for none). The chip tone, its label and the Excel export column all go through it. */
export function displayStatus(status: string | null | undefined): string {
  return (LEGACY_STAY_STATUSES as readonly string[]).includes(status ?? '') ? 'CONFIRMED' : (status ?? '');
}

/** Translation keys (under `bookings.status.*`) per status the Extranet shows — the list chip, the
 *  Excel export and the booking header all read from here. */
export const STATUS_LABEL_KEYS: Record<Exclude<BookingStatus, LegacyStayStatus>, string> = {
  CONFIRMED: 'bookings.status.confirmed',
  CANCELLED: 'bookings.status.cancelled',
  PENDING: 'bookings.status.pending',
};

export function statusLabelKey(status: string | null | undefined): string {
  const shown = displayStatus(status);
  return (STATUS_LABEL_KEYS as Record<string, string>)[shown] ?? shown;
}

/** The query-string filters of `GET /extranet/orders` and its `/summary` — one shape for both,
 *  so the list and its tiles can never disagree on what "the filtered set" is (AC-13.1).
 *  `from`/`to` bound the STAY: a reservation is in the window when it overlaps it
 *  (`checkOut > from && checkIn <= to`, both inclusive dates) — the same rule the Reservations
 *  report prints ("a stay counts in every period it overlaps", T-13.4). `checkInOn` pins the
 *  CHECK-IN date instead (the Arrivals tab). Every field is optional and a blank, null or "all"
 *  sentinel means "no constraint" (see `toOrderQueryParams`). */
export interface OrderListParams {
  from?: string | null;
  to?: string | null;
  /** Exactly this check-in date (ISO); the Arrivals tab's "today". */
  checkInOn?: string | null;
  /** One property of the caller's group; the `ALL_PROPERTIES` sentinel (or blank) = every property. */
  propertyId?: string | null;
  channelType?: ChannelType | 'all' | '' | null;
  status?: BookingStatus | 'all' | '' | null;
  clientId?: string | null;
  /** Case-insensitive contains on reference, guest name and client name (BE-side). */
  search?: string | null;
  /** 1-based; the BE defaults to 1. */
  page?: number | null;
  /** Rows per page; the BE defaults to 20 and caps at 100. */
  size?: number | null;
  /** Where the rooms came from (F3, handoff §11): CONTRACT | SUPPLIER | DIRECT (= no source);
   *  blank, null or "all" = every source. */
  sourceType?: OrderSourceFilter | 'all' | '' | null;
}

/** Real code for "every property" in the property filter — never an empty option value
 *  (handoff §21.3 rule 3). */
export const ALL_PROPERTIES = 'ALL';

/** `GET /extranet/orders/summary` — the four tiles (F3 renders them) over the filtered set. Money
 *  is in `currency` (the group's) and never converted; CANCELLED and PENDING are outside every
 *  money figure and counted on their own. */
export interface OrdersSummary {
  revenue: number;
  adr: number;
  reservations: number;
  cancelled: number;
  pending: number;
  roomNights: number;
  avgStay: number;
  currency: string;
}

const ALL_SENTINELS = new Set(['', 'all', ALL_PROPERTIES]);

function isConstraint(v: string | null | undefined): v is string {
  return typeof v === 'string' && !ALL_SENTINELS.has(v.trim()) && v.trim() !== '';
}

/**
 * Maps the list state onto the exact query string the BE understands: blanks, nulls and the
 * "all" sentinels are dropped (a missing param means "no constraint"), `search` is trimmed,
 * `page`/`size` go out only when given and positive. Pure — the spec pins the mapping.
 */
export function toOrderQueryParams(p: OrderListParams): Record<string, string> {
  const q: Record<string, string> = {};
  if (isConstraint(p.from)) q['from'] = p.from;
  if (isConstraint(p.to)) q['to'] = p.to;
  if (isConstraint(p.checkInOn)) q['checkInOn'] = p.checkInOn;
  if (isConstraint(p.propertyId)) q['propertyId'] = p.propertyId;
  if (isConstraint(p.channelType)) q['channelType'] = p.channelType;
  if (isConstraint(p.status)) q['status'] = p.status;
  if (isConstraint(p.clientId)) q['clientId'] = p.clientId;
  if (isConstraint(p.search)) q['search'] = p.search.trim();
  if (isConstraint(p.sourceType) && isOrderSourceFilter(p.sourceType)) q['sourceType'] = p.sourceType;
  if (p.page != null && p.page > 0) q['page'] = String(Math.floor(p.page));
  if (p.size != null && p.size > 0) q['size'] = String(Math.floor(p.size));
  return q;
}
